import { Button } from '@src/design-system/button';
import { createRoute } from '@src/core/router/paths';
import { Form, Formik, type FormikTouched, type FormikProps } from 'formik';
import { ArrowLeft } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import EditRegistrationInfo from './edit-registration-info';
import EditAdditionalInfo from './edit-additional-info';
import EditAddressInfo from './edit-address-info';
import EditEmergencyContactInformation from './edit-emergency-contact-information';
import { useEffect, useRef } from 'react';
import type { TEmergencyContactInfo } from '@shared/types/member';
import type { TAddress } from '@shared/types/organization';
import {
  additionalInfoSchema,
  addressSchema,
  contactInfoSchema,
  emergencyContactSchema,
  generateAdditionalInfoInitialValue,
  generateAddressFormInitialValue,
  generateContactInfo,
  generateEmergencyContactInfoInitialValue,
  generatePersonalInfoInitialValue,
  type IAdditionalInfo,
  type IPersonalInfo,
  personalInfoSchema,
  type TContactInfoValues,
} from './constants';
import { useEditUserAddress } from '../../services/edit-address';
import { useEditUserEContactInfo } from '../../services/edit-emergency-contact-info';
import EditContactInfo from './edit-contact-info';
import { useEditUserContactInfo } from '../../services/edit-contact-info';
import memberApi from '@shared/services/member-api';
import userApi from '@shared/services/user-api';

const EditUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: userData } = memberApi.useFetchOne({ id: userId });
  const formRegistrationRef = useRef<FormikProps<IPersonalInfo>>(null);
  const formAddressInfoRef = useRef<FormikProps<TAddress>>(null);
  const formContactInfoRef = useRef<FormikProps<TContactInfoValues>>(null);
  const formAdditionalInfoRef = useRef<FormikProps<IAdditionalInfo>>(null);
  const formEmergencyContactInfoRef =
    useRef<FormikProps<TEmergencyContactInfo>>(null);

  const {
    mutateAsync: editUser,
    isPending: editUserPending,
    isSuccess: editUserSuccess,
  } = userApi.useUpdate();
  const {
    mutateAsync: editAddress,
    isPending: editAddressPending,
    isSuccess: editAddressSuccess,
  } = useEditUserAddress();
  const {
    mutateAsync: editUserEContactInfo,
    isPending: editEContactInfoPending,
    isSuccess: editEContactInfoSuccess,
  } = useEditUserEContactInfo();
  const {
    mutateAsync: editUserContactInfo,
    isPending: editContactInfoPending,
    isSuccess: editContactInfoSuccess,
  } = useEditUserContactInfo();
  const {
    mutateAsync: editAdditionalInfo,
    isPending: editAdditionalInfoPending,
    isSuccess: editAdditionalInfoSuccess,
  } = memberApi.useUpdate();

  const handleRegistrationSubmit = (values: IPersonalInfo) => {
    if (userData?.user.id) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { avatar, ...rest } = values;
      editUser({ ...rest, id: userData?.user.id });
    }
  };

  const handleAdditionalSubmit = (values: IAdditionalInfo) => {
    if (userData?.id) {
      editAdditionalInfo({ ...values, id: userData?.id });
    }
  };

  const handleAddressSubmit = (values: TAddress) => {
    editAddress({ ...values, userId: +userId! });
  };

  const handleEmergencyContactSubmit = (values: TEmergencyContactInfo) => {
    editUserEContactInfo({ ...values, userId: +userId! });
  };

  const handleContactInfoSubmit = (values: TContactInfoValues) => {
    editUserContactInfo({
      ...values,
      userId: +userId!,
      id: userData?.contact_info?.id || 0,
    });
  };

  const handleSharedSubmit = async () => {
    const validateOnly = async <T,>(
      formikRef: React.RefObject<FormikProps<T>>
    ) => {
      const formik = formikRef.current;
      if (!formik)
        return { formik: null as FormikProps<T> | null, hasErrors: false };

      const errors = await formik.validateForm();

      // mark all fields as touched so validation messages show
      const valueKeys = Object.keys(formik.values as Record<string, unknown>);
      const touched = valueKeys.reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {} as Record<string, boolean>
      );
      formik.setTouched(touched as unknown as FormikTouched<T>);

      const hasErrors =
        Object.keys(errors as Record<string, unknown>).length > 0;

      return { formik, hasErrors };
    };

    const results = await Promise.all([
      validateOnly(formRegistrationRef),
      validateOnly(formAdditionalInfoRef),
      validateOnly(formAddressInfoRef),
      validateOnly(formEmergencyContactInfoRef),
      validateOnly(formContactInfoRef),
    ]);

    const hasAnyErrors = results.some((r) => r.hasErrors);
    if (hasAnyErrors) return;

    for (const result of results) {
      if (result.formik && result.formik.dirty) {
        await result.formik.submitForm();
      }
    }
  };

  useEffect(() => {
    if (
      editUserSuccess ||
      editAddressSuccess ||
      editEContactInfoSuccess ||
      editAdditionalInfoSuccess ||
      editContactInfoSuccess
    ) {
      navigate(createRoute(`/users/${userId}`));
    }
  }, [
    editUserSuccess,
    editAddressSuccess,
    editEContactInfoSuccess,
    editAdditionalInfoSuccess,
    editContactInfoSuccess,
    navigate,
    userId,
  ]);

  if (!userData) return null;

  return (
    <div className='relative bg-primary-30'>
      <div className='flex justify-between bg-white items-center p-6'>
        <h1 className='text-2xl font-semibold text-gray-1000 flex items-center'>
          <Button
            variant='primaryLink'
            size={'iconSm'}
            onClick={() => navigate(createRoute(`/users/${userId}`))}
          >
            <ArrowLeft />
          </Button>
          <p className='text-2xl font-semibold text-gray-1000'>
            Edit Personal Info
          </p>
        </h1>
      </div>
      <div className='p-5'>
        <Formik
          key={1}
          initialValues={
            generatePersonalInfoInitialValue(userData) as IPersonalInfo
          }
          validationSchema={personalInfoSchema}
          onSubmit={(values) => handleRegistrationSubmit(values)}
          innerRef={formRegistrationRef}
        >
          <Form>
            <EditRegistrationInfo />
          </Form>
        </Formik>
        <Formik
          key={2}
          initialValues={
            generateAdditionalInfoInitialValue(userData) as IAdditionalInfo
          }
          validationSchema={additionalInfoSchema}
          onSubmit={(values) => handleAdditionalSubmit(values)}
          innerRef={formAdditionalInfoRef}
        >
          <Form>
            <EditAdditionalInfo />
          </Form>
        </Formik>
        <Formik
          key={3}
          initialValues={generateAddressFormInitialValue(userData) as TAddress}
          validationSchema={addressSchema}
          onSubmit={(values) => handleAddressSubmit(values)}
          innerRef={formAddressInfoRef}
        >
          <Form>
            <EditAddressInfo />
          </Form>
        </Formik>
        <Formik
          key={4}
          initialValues={
            generateContactInfo({
              personal_email: userData.contact_info?.personal_email,
              personal_phone_number:
                userData.contact_info?.personal_phone_number,
            }) as TContactInfoValues
          }
          validationSchema={contactInfoSchema}
          onSubmit={(values) => handleContactInfoSubmit(values)}
          innerRef={formContactInfoRef}
        >
          <Form>
            <EditContactInfo />
          </Form>
        </Formik>
        <Formik
          key={5}
          initialValues={
            generateEmergencyContactInfoInitialValue(
              userData
            ) as TEmergencyContactInfo
          }
          validationSchema={emergencyContactSchema}
          onSubmit={(values) => handleEmergencyContactSubmit(values)}
          innerRef={formEmergencyContactInfoRef}
        >
          <Form>
            <EditEmergencyContactInformation />
          </Form>
        </Formik>
      </div>
      <div className='bg-white border-t-2 z-50 border-pale-border inset-x-0 bottom-0 sticky'>
        <div className='flex items-center py-4 px-5 mx-auto justify-between'>
          <Button
            variant='primaryOutline'
            onClick={() => navigate(createRoute(`/users/${userId}`))}
          >
            Cancel
          </Button>

          <Button
            type='button'
            onClick={handleSharedSubmit}
            isLoading={
              editUserPending ||
              editAddressPending ||
              editEContactInfoPending ||
              editAdditionalInfoPending ||
              editContactInfoPending
            }
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
